import type { RequestContextDto, RequestContextValues } from '@agent-platform/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { parseContextJson } from '@/pages/request-context';

const NONE = 'none';
const CUSTOM = 'custom';

/**
 * Chooses the request context for a run: none, a saved preset, or custom JSON. Reports `null` while
 * custom JSON is invalid. `expected` lists the variables the target uses (missing ones are flagged).
 */
export function RequestContextPicker({
  tenantId,
  id,
  expected = [],
  onChange,
}: {
  tenantId: string;
  id: string;
  expected?: string[];
  onChange: (values: RequestContextValues | null) => void;
}) {
  const presets = useQuery({
    queryKey: ['request-contexts', tenantId],
    queryFn: () => api<{ items: RequestContextDto[] }>('/request-contexts'),
  });
  const [choice, setChoice] = useState<string>(NONE);
  const [custom, setCustom] = useState('{\n  \n}');
  const list = presets.data?.items ?? [];
  const parsed = parseContextJson(custom);
  const values: RequestContextValues | null =
    choice === NONE
      ? {}
      : choice === CUSTOM
        ? parsed.ok
          ? parsed.values
          : null
        : ((list.find((p) => p.id === choice)?.values as RequestContextValues | undefined) ?? {});
  const key = JSON.stringify(values);
  useEffect(() => {
    onChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report when the values change
  }, [key]);
  const missing = values ? expected.filter((v) => !(v in values)) : [];

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>Request context</Label>
      <Select value={choice} onValueChange={setChoice}>
        <SelectTrigger id={id} aria-label="Request context" className="min-w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>None</SelectItem>
          {list.length > 0 && <SelectSeparator />}
          {list.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.name}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={CUSTOM}>Custom JSON…</SelectItem>
        </SelectContent>
      </Select>
      {choice === CUSTOM && (
        <>
          <Textarea
            aria-label="Custom request context (JSON)"
            className="min-h-24 font-mono text-xs"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            aria-invalid={!parsed.ok}
          />
          {!parsed.ok && <p className="text-destructive text-xs">{parsed.error}</p>}
        </>
      )}
      {expected.length > 0 && (
        <p className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs">
          Uses
          {expected.map((v) => (
            <Badge
              key={v}
              variant={missing.includes(v) ? 'outline' : 'secondary'}
              className="font-mono font-normal"
            >
              {v}
            </Badge>
          ))}
          {missing.length > 0 && <span>· unset ones stay as written</span>}
        </p>
      )}
      {list.length === 0 && choice === NONE && (
        <p className="text-muted-foreground text-xs">
          Save reusable sets on the{' '}
          <Link to="/request-context" className="underline">
            Request context
          </Link>{' '}
          page.
        </p>
      )}
    </div>
  );
}
